import { Button, Tooltip } from '@shared/components';
import { useToast } from '@shared/components/toast';
import {
  TrackingPauseSource,
  useSetMovementTrackingPausedMutation,
} from '@shared/generated/graphql';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import { PiPause, PiPlay } from 'react-icons/pi';

type MovementTrackingPauseControlProps = {
  movementId: string;
  isPaused: boolean;
  source?: TrackingPauseSource;
  onPausedChange?: () => void;
};

export function MovementTrackingPauseControl({
  movementId,
  isPaused,
  source = TrackingPauseSource.MANUAL,
  onPausedChange,
}: MovementTrackingPauseControlProps) {
  const [setMovementTrackingPaused, { loading }] =
    useSetMovementTrackingPausedMutation();
  const { sendToast } = useToast();
  const { track } = useAnalytics();

  async function togglePause() {
    const paused = !isPaused;
    let succeeded = false;

    try {
      const res = await setMovementTrackingPaused({
        variables: { input: { movementId, paused, source } },
      });
      succeeded = Boolean(res.data?.setMovementTrackingPaused?.success);
    } catch {
      succeeded = false;
    }

    if (succeeded) {
      track(paused ? 'Paused tracking' : 'Resumed tracking');
      onPausedChange?.();
      return;
    }

    sendToast('Failed to update tracking', {
      description: 'Something went wrong updating tracking for this movement.',
      variant: 'error',
    });
  }

  return (
    <Tooltip content={isPaused ? 'Resume tracking' : 'Pause tracking'}>
      <Button
        size="xs"
        variant="secondary"
        onPress={togglePause}
        isDisabled={loading}
        icon={isPaused ? <PiPlay /> : <PiPause />}
        iconPosition="right"
        className="flex flex-row items-center gap-1"
      >
        {isPaused ? 'Resume' : 'Pause'}
      </Button>
    </Tooltip>
  );
}
